import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crosshair, Plus, X, Loader } from 'lucide-react';
import { usePentest } from '../contexts/PentestContext';

// Create-application inline modal. Fields modeled on the compliance integration
// forms (teal focus ring, rounded inputs).
const CreateApplicationModal = ({ isOpen, onClose, onCreate, creating }) => {
  const [name, setName] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [formError, setFormError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!name.trim()) {
      setFormError('Application name is required');
      return;
    }
    // Required, not optional: the pentest engine is white-box only and cannot run without
    // source code, so an application with no repository can never actually be scanned.
    // Collecting it here avoids a confusing failure later, at launch time.
    if (!repoUrl.trim()) {
      setFormError('Repository URL is required — pentests analyze source code alongside the running app');
      return;
    }
    const created = await onCreate({ name: name.trim(), repoUrl: repoUrl.trim() });
    if (created) {
      setName('');
      setRepoUrl('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">New Application</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={creating}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Application Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Web App"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              disabled={creating}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Repository URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="https://github.com/org/repo"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              disabled={creating}
            />
            <p className="mt-2 text-xs text-gray-500">
              Pentests are <span className="font-medium">white-box</span>: the engine reads your
              source code to find attack paths, then proves them against the running app. The
              repository is cloned, mounted read-only into an ephemeral scan container, and its
              contents are sent to your configured AI provider as analysis context. Only link a
              repository you are authorized to share on these terms.
            </p>
          </div>
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {formError}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
              disabled={creating}
            >
              {creating ? (<><Loader className="w-4 h-4 animate-spin" />Creating...</>) : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const {
    applications,
    loadingApplications,
    creatingApplication,
    loadApplications,
    createApplication,
    error,
  } = usePentest();
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const getTargetCount = (app) => (Array.isArray(app.targets) ? app.targets.length : (app.targetCount ?? 0));

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl text-gray-900">Applications</h1>
          <p className="text-gray-600 mt-1">
            Autonomous pentesting of your running web applications
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2 min-h-[44px] rounded-lg shadow transition flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New Application
        </button>
      </div>

      {error && (
        <div className="mb-6 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">{error}</div>
      )}

      {loadingApplications ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
          <Crosshair className="w-12 h-12 text-teal-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications yet</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create an application and add a running target URL to start an autonomous pentest.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-lg transition inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Application
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
          {applications.map((app) => (
            <div
              key={app.id}
              onClick={() => navigate(`/applications/${app.id}`)}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col min-h-[200px] w-full cursor-pointer hover:border-teal-400 transition-transform duration-200 ease-in-out hover:scale-105 hover:shadow-lg"
            >
              <div className="mb-2">
                <div className="font-bold text-gray-900 text-lg leading-tight truncate flex items-center gap-2">
                  <Crosshair className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  {app.name}
                </div>
                {app.repoUrl && (
                  <a
                    href={app.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:underline truncate block mt-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {app.repoUrl}
                  </a>
                )}
              </div>
              <div className="flex items-end gap-8 mt-4 mb-2">
                <div className="flex flex-col items-start">
                  <span className="text-2xl text-gray-900 leading-none">{getTargetCount(app)}</span>
                  <span className="text-xs text-gray-500 uppercase tracking-wide mt-1">Targets</span>
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-2xl text-gray-900 leading-none">
                    {Array.isArray(app.pentests) ? app.pentests.length : (app.pentestCount ?? 0)}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-wide mt-1">Pentests</span>
                </div>
              </div>
              <div className="border-t border-gray-200 my-2"></div>
              <div className="mt-auto pt-1 text-xs text-gray-500">
                {app.latest_pentest_time || app.createdAt
                  ? new Date(app.latest_pentest_time || app.createdAt).toLocaleDateString('en-GB')
                  : '-'}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateApplicationModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={createApplication}
        creating={creatingApplication}
      />
    </div>
  );
};

export default ApplicationsPage;
